import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { useAuth } from '@/lib/auth';
import { useState } from 'react';
import { Menu, X, Sun, Moon, Globe, LogOut, Shield, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificationBell from './NotificationBell';
import { useCart } from '@/lib/cart';

const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const { user, isAdmin, signOut } = useAuth();
  const { items } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/gallery', label: t('gallery') },
    { path: '/about', label: t('about') },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b-0">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Melody Art" className="h-9 w-auto" />
          <span className="font-display text-lg font-bold text-gradient hidden sm:inline">Melody Art</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                isActive(link.path) ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setLang(lang === 'en' ? 'ar' : 'en')} className="text-muted-foreground hover:text-primary h-9 w-9">
            <Globe className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-muted-foreground hover:text-primary h-9 w-9">
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>

          {/* Cart */}
          <Link to="/cart">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary h-9 w-9 relative">
              <ShoppingBag className="h-4 w-4" />
              {items.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">
                  {items.length}
                </span>
              )}
            </Button>
          </Link>

          {user && <NotificationBell />}

          {isAdmin && (
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary h-9 w-9">
                <Shield className="h-4 w-4" />
              </Button>
            </Link>
          )}

          {user ? (
            <div className="flex items-center gap-1">
              <Link to="/orders">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary text-xs hidden sm:flex">
                  {t('myOrders')}
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={signOut} className="text-muted-foreground hover:text-primary h-9 w-9">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Link to="/auth">
              <Button size="sm" className="gradient-accent text-primary-foreground text-xs h-8 px-3">
                {t('login')}
              </Button>
            </Link>
          )}

          <Button variant="ghost" size="icon" className="md:hidden text-muted-foreground h-9 w-9" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden glass-panel border-t-0 animate-fade-in-up">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)}
                className={`py-2 text-sm font-medium transition-colors ${isActive(link.path) ? 'text-primary' : 'text-muted-foreground'}`}>
                {link.label}
              </Link>
            ))}
            {user && (
              <Link to="/orders" onClick={() => setMobileOpen(false)} className="py-2 text-sm text-muted-foreground">
                {t('myOrders')}
              </Link>
            )}
            <Link to="/cart" onClick={() => setMobileOpen(false)} className="py-2 text-sm text-muted-foreground flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" /> {t('cart')} {items.length > 0 && `(${items.length})`}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
